import React, { useEffect, useState } from "react";

import { getUserFacingApiErrorMessage, useTranslation } from "utils";

import { Form, Input, Message, Modal } from "components";

import { ABOVE_SWITCHER_POPOVER_Z_INDEX } from "pages/app/crop-mix-composer/utils/cropMixComposer";

type MixNameModalProps = {
  showModal: boolean;
  formName: string;
  dataTestId: string;
  title: string;
  okText: string;
  submitErrorMessage: string;
  mixName: string;
  takenNames: string[];
  onSubmit: (name: string) => Promise<void>;
  onCancel: () => void;
};

export const MixNameModal = ({
  showModal,
  formName,
  dataTestId,
  title,
  okText,
  submitErrorMessage,
  mixName,
  takenNames,
  onSubmit,
  onCancel,
}: MixNameModalProps) => {
  const { t } = useTranslation("cropMixComposer");

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [form] = Form.useForm<{ mixName: string }>();

  useEffect(() => {
    if (showModal) form.setFieldValue("mixName", mixName);
  }, [form, mixName, showModal]);

  const handleSubmit = async () => {
    let submittedName: string;
    try {
      ({ mixName: submittedName } = await form.validateFields());
    } catch {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(submittedName.trim());
    } catch (submitError) {
      Message({ content: getUserFacingApiErrorMessage(submitError, submitErrorMessage), icon: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form form={form} name={formName}>
      <Modal
        className="crop-mix-action-modal"
        dataTestId={dataTestId}
        zIndex={ABOVE_SWITCHER_POPOVER_Z_INDEX}
        showModal={showModal}
        title={title}
        okText={okText}
        cancelText={t("mixNameModal.cancel")}
        confirmLoading={isSubmitting}
        onOk={handleSubmit}
        onCancel={onCancel}
        content={
          <Input
            dataTestId={`${dataTestId}-name-input`}
            name="mixName"
            label={t("mixNameModal.nameLabel")}
            initialValue={mixName}
            rules={[
              { required: true, whitespace: true },
              {
                validator: (_, value) => {
                  if (takenNames.includes(value.trim())) return Promise.reject(new Error(t("cropMixSelectionModal.errors.mixNameUnique")));
                  return Promise.resolve();
                },
              },
            ]}
          />
        }
      />
    </Form>
  );
};
